import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

type InsiderBuyingTop5Params = {
  symbol: string;
  lang: Lang;
};

export const TradeInfoSchema = z.object({
  tradeAt: z.string().nullish(),
  tradeNumber: z.number().nullish(),
  tradeCash: z.number().nullish(),
  action: z.enum(["S", "B"]).nullish(),
  actionSeries: z
    .string()
    .regex(/^[(S|B)]\d+$/)
    .nullish(),
  price: z.number().nullish(),
});

export const InsiderDataSchema = z.record(z.array(TradeInfoSchema)).optional();

export const DropDownItemSchema = z.object({
  member: z.string().nullish(),
  title: z.string().nullish(),
  visible: z.boolean().nullish(),
});

export const Top5DataSchema = z.object({
  data: z.object({
    point: InsiderDataSchema.nullish(),
    dropDown: z.array(DropDownItemSchema).nullish(),
  }),
  message: z.string(),
});

export type TradeInfo = z.infer<typeof TradeInfoSchema>;
export type InsiderData = z.infer<typeof InsiderDataSchema>;
export type DropDownItem = z.infer<typeof DropDownItemSchema>;
export type Top5Data = z.infer<typeof Top5DataSchema>["data"];

export const getInsiderBuyingTop5 = async ({
  symbol,
  lang,
}: InsiderBuyingTop5Params): Promise<Top5Data> => {
  const endpoint = `/insider-buying/top-five-member/${symbol}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return Top5DataSchema.parse(await res.json()).data;
};

export const useInsiderBuyingTop5 = ({ symbol }: { symbol: string }) => {
  const { lang } = useLang();
  return useQuery(
    ["insider-buying-top5", symbol, lang],
    () => getInsiderBuyingTop5({ symbol, lang }),
    { enabled: !!symbol, refetchOnWindowFocus: false }
  );
};
